import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { auth, coAdmins } from '@/lib/api';
import { setStoredUser, setStoredToken, initiateGoogleLogin } from '@/lib/auth';
import { VALID_PLAN_IDS, isValidBillingCycle } from '@/components/PricingSection';
import { Clock, Loader2, Mail, Building2, Users, Phone, BookOpen, Calendar, ArrowLeft } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

// Read a validated `plan` query param (e.g. ?plan=primary) carried over from
// the public pricing page. Returns null when absent or not a known plan id.
function getPlanFromSearchParams(searchParams) {
  const plan = searchParams.get('plan');
  return VALID_PLAN_IDS.includes(plan) ? plan : null;
}

// Build a path to an auth page, preserving a validated plan param (and the
// annual billing-cycle flag) so the visitor's pricing-page choices survive
// navigation between /register and /login.
function authPath(path, planParam, cycleParam) {
  const params = [];
  if (planParam) params.push(`plan=${planParam}`);
  if (cycleParam === 'annual') params.push('cycle=annual');
  return params.length ? `${path}?${params.join('&')}` : path;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'toc',
    // Sub-specific fields
    phone: '',
    subjects: '',
    availability: '',
    // School admin-specific fields
    contact_position: '',
    termsAccepted: false
  });
  
  // Read role from URL parameter on mount
  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam && (roleParam === 'employer' || roleParam === 'toc')) {
      setFormData(prev => ({ ...prev, role: roleParam }));
    }
  }, [searchParams]);

  // The visitor's plan choice from the public pricing page (?plan=<id>), if any.
  // Preserved across every navigation edge on this page (back button, login
  // link, auto-login failure, "already registered" redirect).
  const planParam = getPlanFromSearchParams(searchParams);

  // Annual billing chosen on the public pricing page (?cycle=annual), if any.
  // Monthly is the default; only 'annual' is carried through the auth flow.
  const cycleParam = isValidBillingCycle(searchParams.get('cycle'))
    ? searchParams.get('cycle')
    : null;

  // Co-admin invite: if a token is present, prefill the email, lock the role to
  // employer (co-admins are School Admin accounts), and accept after signup.
  const inviteToken = searchParams.get('invite_token');
  useEffect(() => {
    if (!inviteToken) return;
    const loadInvite = async () => {
      try {
        const res = await coAdmins.getInvite(inviteToken);
        setFormData(prev => ({ ...prev, email: res.data.email || prev.email, role: 'employer' }));
      } catch (_e) {
        toast.error('This co-admin invite link is invalid or has expired.');
      }
    };
    loadInvite();
  }, [inviteToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Validate phone number input (digits only)
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      return;
    }
    // Validate max length for subjects and availability
    if ((name === 'subjects' || name === 'availability') && value.length > 100) {
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleRoleChange = (value) => {
    setFormData(prev => ({ ...prev, role: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    // Validate first/last name for Subs
    if (formData.role === 'toc') {
      if (!formData.first_name?.trim() || !formData.last_name?.trim()) {
        toast.error('Please enter both first and last name');
        return;
      }
    } else {
      if (!formData.name?.trim()) {
        toast.error('Please enter your full name');
        return;
      }
    }
    
    // Validate phone if provided
    if (formData.phone && formData.phone.length !== 10) {
      toast.error('Phone number must be 10 digits');
      return;
    }
    
    setIsLoading(true);
    
    // Normalize email to lowercase (backend stores lowercase)
    const normalizedEmail = formData.email.toLowerCase().trim();
    
    // Build registration data
    const registrationData = {
      ...formData,
      email: normalizedEmail,
      // For Subs, set name from first/last name
      name: formData.role === 'toc'
        ? `${formData.first_name.trim()} ${formData.last_name.trim()}`
        : formData.name.trim()
    };

    // Free-plan signup signal: employers who didn't pick a paid plan (or
    // explicitly picked the free one) start on Head Start at registration —
    // the backend activates it and sends the Head Start welcome email.
    // Paid picks pass their plan id (welcome email then comes from the
    // checkout webhook). Co-admin invites send nothing — they ride the
    // school owner's subscription, not their own.
    if (formData.role === 'employer' && !inviteToken) {
      registrationData.selected_plan = planParam ?? 'head_start';
    }
    
    try {
      // Register the user
      await auth.register(registrationData);
      
      // Registration succeeded! Now try auto-login
      try {
        const loginResponse = await auth.login({
          email: normalizedEmail,
          password: formData.password
        });
        
        const { token, user } = loginResponse.data;
        setStoredToken(token);
        setStoredUser(user);

        // If this signup came from a co-admin invite, accept it now.
        if (inviteToken && user.role === 'employer') {
          try {
            const acc = await coAdmins.acceptInvite(inviteToken);
            toast.success(`Account created! You're now a co-admin of ${acc.data.school_name}.`);
          } catch (_e) {
            toast.success('Account created successfully!');
          }
          navigate('/employer');
          return;
        }

        toast.success('Account created successfully! Welcome to ExpressSub!', {
          description: 'A confirmation email has been sent to your inbox.'
        });
        // If the visitor picked a plan on the public pricing page, land them
        // on the billing page with that plan pre-selected instead of the dashboard.
        if (planParam && user.role === 'employer') {
          navigate(
            `/employer/billing?plan=${planParam}` +
              (cycleParam === 'annual' ? '&cycle=annual' : '')
          );
        } else {
          navigate(user.role === 'employer' ? '/employer' : '/toc');
        }
      } catch (loginError) {
        // Registration succeeded but auto-login failed - still a success!
        toast.success('Account created successfully!', {
          description: 'Please log in with your credentials.'
        });
        navigate(authPath('/login', planParam, cycleParam));
      }
    } catch (error) {
      // Check if the error is "Email already registered" - that means a previous attempt succeeded
      const errorMessage = error.response?.data?.detail || '';
      if (errorMessage.includes('already registered')) {
        toast.info('This email is already registered. Please log in instead.', {
          description: 'If you forgot your password, contact support.'
        });
        // Preserve the chosen plan so it survives the login step too.
        navigate(authPath('/login', planParam, cycleParam));
      } else {
        toast.error(errorMessage || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGoogleRegister = () => {
    initiateGoogleLogin(formData.role, planParam, cycleParam);
  };
  
  // Format phone for display
  const formatPhone = (phone) => {
    if (!phone) return '';
    if (phone.length <= 3) return phone;
    if (phone.length <= 6) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
  };
  
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(authPath('/login', planParam, cycleParam))}
            data-testid="back-to-login-btn"
            className="text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link to="/" className="flex items-center gap-2 w-fit">
            <BrandLogo variant="black" size="lg" />
          </Link>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <Card className="w-full max-w-md border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="text-left">
            <CardTitle className="text-2xl font-bold font-['Manrope'] text-slate-900">
              Create your account
            </CardTitle>
            <CardDescription className="text-slate-600">
              Get started with ExpressSub today
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Role Selection */}
              <div className="space-y-3">
                <Label className="text-slate-700">I am a...</Label>
                <RadioGroup 
                  value={formData.role} 
                  onValueChange={handleRoleChange}
                  className="grid grid-cols-2 gap-4"
                >
                  <div>
                    <RadioGroupItem 
                      value="employer" 
                      id="employer" 
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="employer"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-slate-200 bg-white p-4 hover:bg-slate-50 peer-data-[state=checked]:border-slate-900 peer-data-[state=checked]:bg-slate-50 cursor-pointer transition-all"
                      data-testid="register-role-employer"
                    >
                      <Building2 className="h-6 w-6 mb-2 text-slate-600" />
                      <span className="text-sm font-medium text-slate-900">School Admin</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem 
                      value="toc" 
                      id="toc" 
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="toc"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-slate-200 bg-white p-4 hover:bg-slate-50 peer-data-[state=checked]:border-slate-900 peer-data-[state=checked]:bg-slate-50 cursor-pointer transition-all"
                      data-testid="register-role-toc"
                    >
                      <Users className="h-6 w-6 mb-2 text-slate-600" />
                      <span className="text-sm font-medium text-slate-900">Substitute</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Name fields - different for Sub vs Employer */}
              {formData.role === 'toc' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-slate-700">First Name</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                      data-testid="register-first-name-input"
                      className="rounded-md"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-slate-700">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      placeholder="Smith"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                      data-testid="register-last-name-input"
                      className="rounded-md"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    data-testid="register-name-input"
                    className="rounded-md"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  data-testid="register-email-input"
                  className="rounded-md"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  disabled={isLoading}
                  data-testid="register-password-input"
                  className="rounded-md"
                />
                <p className="text-xs text-slate-500">At least 6 characters</p>
              </div>
              
              {/* Conditional fields based on role */}
              {formData.role === 'toc' && (
                <>
                  <Separator className="my-4" />
                  <p className="text-sm text-slate-600 font-medium">Profile Information (visible to schools)</p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formatPhone(formData.phone)}
                      onChange={handleChange}
                      disabled={isLoading}
                      data-testid="register-phone-input"
                      className="rounded-md"
                    />
                    <p className="text-xs text-slate-500">10 digits</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subjects" className="text-slate-700 flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Subjects/Specializations
                    </Label>
                    <Textarea
                      id="subjects"
                      name="subjects"
                      placeholder="e.g., Math K-5, Special Education, ESL"
                      value={formData.subjects}
                      onChange={handleChange}
                      disabled={isLoading}
                      data-testid="register-subjects-input"
                      className="rounded-md resize-none h-20"
                      maxLength={100}
                    />
                    <p className="text-xs text-slate-500">{formData.subjects.length}/100 characters</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="availability" className="text-slate-700 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Availability
                    </Label>
                    <Textarea
                      id="availability"
                      name="availability"
                      placeholder="e.g., Mon-Fri 8am-3pm, Available on short notice"
                      value={formData.availability}
                      onChange={handleChange}
                      disabled={isLoading}
                      data-testid="register-availability-input"
                      className="rounded-md resize-none h-20"
                      maxLength={100}
                    />
                    <p className="text-xs text-slate-500">{formData.availability.length}/100 characters</p>
                  </div>
                </>
              )}
              
              {/* Terms and Privacy consent checkbox */}
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms-consent"
                  checked={formData.termsAccepted}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, termsAccepted: checked }))}
                  data-testid="terms-consent-checkbox"
                  className="mt-0.5"
                />
                <label htmlFor="terms-consent" className="text-xs text-slate-600 leading-snug cursor-pointer">
                  I have read and agree to the{' '}
                  <Link to="/terms" target="_blank" className="underline text-emerald-600 hover:text-emerald-700">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" className="underline text-emerald-600 hover:text-emerald-700">Privacy Policy</Link>
                </label>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-slate-900 hover:bg-slate-800 rounded-md"
                disabled={isLoading || !formData.termsAccepted}
                data-testid="register-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
            
            <div className="relative my-6">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-slate-500">
                or continue with
              </span>
            </div>
            
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-md border-slate-300"
              onClick={handleGoogleRegister}
              disabled={isLoading}
              data-testid="register-google-btn"
            >
              <Mail className="mr-2 h-4 w-4" />
              Sign up with Google
            </Button>
            
            <p className="mt-6 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link
                to={authPath('/login', planParam, cycleParam)}
                className="font-medium text-emerald-600 hover:text-emerald-700"
                data-testid="register-login-link"
              >
                Sign in
              </Link>
            </p>
            
            <p className="mt-4 text-center text-xs text-slate-400">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-slate-600">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="underline hover:text-slate-600">Privacy Policy</Link>.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
